import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import FMCGImage from '../assets/images/expertise/FMCG.jpg'
import ITPeripheralsImage from '../assets/images/expertise/IT-Peripherals.jpg'
import CosmeticsImage from '../assets/images/expertise/Cosmetics.jpg'
import StationeryPackagingImage from '../assets/images/expertise/Stationery-Packaging.jpg'
import PharmaceuticalsImage from '../assets/images/expertise/Pharmaceuticals.jpg'
import TextileGarmentsImage from '../assets/images/expertise/Textile-Garments.jpg'
import ElectronicElectricalsImage from '../assets/images/expertise/Electronic-Electricals.jpg'
import EngineeringGoodsImage from '../assets/images/expertise/Engineering-Goods.jpg'
import AutomobileAncillaryImage from '../assets/images/expertise/Automobile-Ancillary.jpg'
import eCommerceImage from '../assets/images/expertise/eCommerce.jpg'
import eCommerceIcon from '../assets/images/expertise/eCommerce-icon.png'
import CosmeticsIcon from '../assets/images/expertise/Cosmetics-icon.png'
import StationeryPackagingIcon from '../assets/images/expertise/Stationery-Packaging-icon.png'
import PharmaceuticalsIcon from '../assets/images/expertise/Pharmaceuticals-icon.png'
import ITPeripheralsIcon from '../assets/images/expertise/IT-Peripherals-icon.png'
import TextileGarmentsIcon from '../assets/images/expertise/Textile-Garments-icon.png'
import FMCGIcon from '../assets/images/expertise/FMCG-icon.png'
import ElectronicElectricalsIcon from '../assets/images/expertise/Electronic-Electricals-icon.png'
import EngineeringGoodsIcon from '../assets/images/expertise/Engineering-Goods-icon.png'
import AutomobileAncillaryIcon from '../assets/images/expertise/Automobile-Ancillary-icon.png'

const industries = [
  {
    name: 'eCommerce',
    image: eCommerceImage,
    icon: eCommerceIcon,
    link: '/expertise/ecommerce/',
    description:
      'Reliable eCommerce logistics with fast deliveries, seamless order fulfillment, real-time tracking, and efficient supply chain solutions for growing online businesses.'
  },
  {
    name: 'Cosmetics',
    image: CosmeticsImage,
    icon: CosmeticsIcon,
    link: '/expertise/cosmetics/',
    description:
      'Specialized logistics solutions for cosmetics and beauty products, ensuring safe handling, timely deliveries, and efficient distribution across markets.'
  },
  {
    name: 'Stationery & Packaging',
    image: StationeryPackagingImage,
    icon: StationeryPackagingIcon,
    link: '/expertise/stationery-packaging/',
    description:
      'Efficient transportation and distribution of stationery and packaging materials with reliable deliveries, optimized operations, and nationwide logistics support.'
  },
  {
    name: 'Pharmaceuticals',
    image: PharmaceuticalsImage,
    icon: PharmaceuticalsIcon,
    link: '/expertise/pharmaceuticals/',
    description:
      'Dependable pharmaceutical logistics with secure transportation, careful handling, timely distribution, and reliable supply chain management for healthcare businesses.'
  },
  {
    name: 'IT & Peripherals',
    image: ITPeripheralsImage,
    icon: ITPeripheralsIcon,
    link: '/expertise/it-peripherals/',
    description:
      'Smart logistics solutions for IT equipment and peripherals, delivering technology products safely, efficiently, and on time across destinations.'
  },
  {
    name: 'Textile & Garments',
    image: TextileGarmentsImage,
    icon: TextileGarmentsIcon,
    link: '/expertise/textile-garments/',
    description:
      'Streamlined textile and garment logistics with efficient transportation, careful shipment handling, and dependable distribution for the fashion industry.'
  },
  {
    name: 'FMCG',
    image: FMCGImage,
    icon: FMCGIcon,
    link: '/expertise/fmcg/',
    description:
      'Reliable FMCG logistics designed for faster distribution, efficient inventory movement, consistent deliveries, and seamless supply chain operations.'
  },
  {
    name: 'Electronic & Electricals',
    image: ElectronicElectricalsImage,
    icon: ElectronicElectricalsIcon,
    link: '/expertise/electronic-electricals/',
    description:
      'Specialized logistics for electronic and electrical products, ensuring secure handling, efficient transportation, and timely deliveries across India.'
  },
  {
    name: 'Engineering Goods',
    image: EngineeringGoodsImage,
    icon: EngineeringGoodsIcon,
    link: '/expertise/engineering-goods/',
    description:
      'Comprehensive engineering goods logistics with efficient freight movement, secure cargo handling, and dependable supply chain management for businesses.'
  },
  {
    name: 'Automobile Ancillary',
    image: AutomobileAncillaryImage,
    icon: AutomobileAncillaryIcon,
    link: '/expertise/automobile-ancillary/',
    description:
      'Efficient automobile logistics solutions for automotive components, ensuring reliable transportation, timely deliveries, and uninterrupted supply chain operations.'
  }
]

// Each slide displays two consecutive industries
const slides = industries.map((item, index) => [
  item,
  industries[(index + 1) % industries.length]
])

const IndustryCard = ({ item, reverse }) => {
  const industryUrl = `/industry-expertise/${item.name
    .trim()
    .replace(/\s+/g, '-')}`

  return (
    <div
      className={`group relative flex min-h-[460px] flex-col overflow-hidden bg-white sm:min-h-[480px] lg:h-[520px] lg:min-h-0 ${
        reverse ? 'lg:flex-col-reverse' : ''
      }`}
    >
      {/* IMAGE SECTION */}
      <div className='h-[230px] w-full shrink-0 overflow-hidden bg-gray-100 sm:h-[240px] lg:h-1/2'>
        <a href={industryUrl} className='block h-full w-full'>
          <img
            src={item.image}
            alt={item.name}
            loading='lazy'
            className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
          />
        </a>
      </div>

      {/* CIRCULAR ICON */}
      <div className='absolute left-1/2 top-[230px] z-20 flex h-[76px] w-[76px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_5px_20px_rgba(0,0,0,0.15)] transition-all duration-300 group-hover:scale-110 sm:top-[240px] sm:h-[90px] sm:w-[90px] lg:top-1/2'>
        <a
          href={industryUrl}
          aria-label={`Explore ${item.name}`}
          className='flex h-full w-full items-center justify-center rounded-full'
        >
          <img
            src={item.icon}
            alt={`${item.name} icon`}
            loading='lazy'
            className='h-11 w-11 object-contain sm:h-14 sm:w-14'
          />
        </a>
      </div>

      {/* DESCRIPTION SECTION */}
      <div className='flex min-h-[230px] flex-1 flex-col items-center justify-center bg-white px-5 pb-7 pt-12 text-center sm:px-7 sm:pt-14 lg:h-1/2 lg:min-h-0 lg:px-6 lg:py-8'>
        <h3 className='mb-3 text-xl font-medium leading-tight text-[#07509e] transition-colors duration-300 group-hover:text-orange-500 sm:mb-4 sm:text-2xl'>
          <a href={industryUrl}>{item.name}</a>
        </h3>

        <p className='max-w-lg text-sm leading-7 text-gray-500 sm:text-base'>
          {item.description}
        </p>
      </div>
    </div>
  )
}

const IndustryExpertise = () => {
  const [current, setCurrent] = useState(0)
  const [direction, setDirection] = useState(1)
  const [isPaused, setIsPaused] = useState(false)

  const nextSlide = () => {
    setDirection(1)
    setCurrent(prev => (prev + 1) % slides.length)
  }

  const prevSlide = () => {
    setDirection(-1)
    setCurrent(prev => (prev - 1 + slides.length) % slides.length)
  }

  // Automatic slider
  useEffect(() => {
    if (isPaused) return

    const interval = setInterval(() => {
      setDirection(1)

      setCurrent(prev => {
        if (prev >= slides.length - 1) {
          return 0 // Restart from first slide
        }

        return prev + 1
      })
    }, 3500)

    return () => clearInterval(interval)
  }, [isPaused])

  return (
    <section className='relative overflow-hidden bg-white py-5 sm:py-6 lg:py-7'>
      <div className='mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 sm:px-6 md:px-8 lg:grid-cols-12 lg:gap-10 lg:px-10'>
        {/* LEFT CONTENT */}
        <div className='lg:col-span-4'>
          <span className='mb-3 inline-block text-xs font-semibold uppercase tracking-[3px] text-[#211f57] sm:mb-4 sm:text-sm'>
            What We Do
          </span>

          <h2 className='mb-5 text-[42px] font-light leading-[1.05] tracking-tight text-[#f80606] sm:text-[55px] md:text-[65px] lg:mb-7 lg:text-[75px]'>
            <span className='block text-base font-normal tracking-normal text-gray-800 sm:text-lg'>
              Industry
            </span>
            Expertise
          </h2>

          <p className='max-w-md text-sm leading-7 text-gray-600 sm:text-base sm:leading-8'>
            Delivering smarter logistics solutions tailored to your industry.
            With reliable transportation, advanced technology, and nationwide
            connectivity, we keep your business moving forward.
          </p>

          {/* EXPLORE BUTTON */}
          <a
            href='/industry-expertise/'
            className='mt-6 inline-flex items-center justify-center gap-3 rounded-sm bg-[#211f57] px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:bg-[#302d78] hover:shadow-xl sm:mt-8 sm:px-8'
          >
            Explore Our Expertise
          </a>

          {/* SLIDER CONTROLS */}
          <div className='mt-8 flex items-center gap-4 sm:mt-10 lg:mt-14'>
            <button
              onClick={prevSlide}
              aria-label='Previous slide'
              className='flex h-10 w-10 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-[#211f57]'
            >
              <ChevronLeft size={25} />
            </button>

            <div className='flex items-center gap-3'>
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setDirection(index > current ? 1 : -1)
                    setCurrent(index)
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={current === index ? 'true' : undefined}
                  className={`rounded-full transition-all duration-300 ${
                    current === index
                      ? 'h-3 w-7 bg-[#211f57]'
                      : 'h-2.5 w-2.5 bg-gray-300 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextSlide}
              aria-label='Next slide'
              className='flex h-10 w-10 items-center justify-center rounded-full text-gray-400 transition hover:bg-gray-100 hover:text-[#211f57]'
            >
              <ChevronRight size={25} />
            </button>
          </div>
        </div>

        {/* RIGHT SIDE SLIDER */}
        <div
          className='min-w-0 overflow-hidden bg-white lg:col-span-8'
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className='relative h-[900px] w-full overflow-hidden bg-white sm:h-[520px] lg:h-[520px]'>
            <AnimatePresence initial={false} custom={direction} mode='sync'>
              <motion.div
                key={current}
                custom={direction}
                variants={{
                  enter: dir => ({
                    x: dir > 0 ? '100%' : '-100%'
                  }),

                  center: {
                    x: '0%'
                  },

                  exit: dir => ({
                    x: dir > 0 ? '-100%' : '100%'
                  })
                }}
                initial='enter'
                animate='center'
                exit='exit'
                transition={{
                  x: {
                    duration: 0.8,
                    ease: 'easeInOut'
                  }
                }}
                className='absolute inset-0 grid w-full grid-cols-1 bg-white sm:grid-cols-2'
                style={{ willChange: 'transform' }}
              >
                {slides[current].map((item, index) => (
                  <IndustryCard
                    key={item.name}
                    item={item}
                    reverse={index === 1}
                  />
                ))}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}

export default IndustryExpertise
